import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { AppDispatch } from '@/app/store';
import { selectCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchParticipantFullDetailsData, openParticipantSidebar } from '../store/analyticsSlice';
import type { AttendeeEngagementDto } from '../types';
import { Pagination } from '@/components/ui/pagination.tsx';
import { useTranslation } from 'react-i18next';
const columnHelper = createColumnHelper<AttendeeEngagementDto>();

interface ParticipantDetailTableProps {
  participants: AttendeeEngagementDto[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

export const ParticipantDetailTable = ({
  participants,
  totalElements,
  totalPages,
  currentPage,
  onPageChange,
  isLoading,
}: ParticipantDetailTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { sessionId } = useParams<{ sessionId: string }>();
  const workspace = useSelector(selectCurrentWorkspace);
  const { t } = useTranslation('webinars');
  const { t: tA } = useTranslation('analytics');
  const handleOpenParticipantDetails = (attendeeIdentifier: number) => {
    if (workspace?.id && sessionId) {
      dispatch(openParticipantSidebar(attendeeIdentifier));
      dispatch(
        fetchParticipantFullDetailsData({
          workspaceId: workspace.id,
          sessionId: Number.parseInt(sessionId),
          attendeeId: attendeeIdentifier,
        }),
      );
    }
  };

  const columns = [
    columnHelper.accessor('fullName', {
      header: tA('participantDetail.table.name'),
      cell: (info) => (
        <button
          onClick={() => handleOpenParticipantDetails(info.row.original.attendeeId)}
          className="flex items-center gap-3 py-1 text-left hover:opacity-70 transition-opacity"
        >
          <div className="w-8 h-8 rounded-full surface-subtle flex items-center justify-center text-[10px] font-bold text-role-tertiary uppercase">
            {info.getValue()?.charAt(0)}
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-role-default text-sm">{info.getValue()}</span>
            <span className="text-role-tertiary text-[11px]">{info.row.original.email}</span>
          </div>
        </button>
      ),
    }),
    columnHelper.accessor('offerInteractionStatus', {
      header: tA('participantDetail.table.conversion'),
      cell: (info) => {
        const status = info.getValue();
        if (status === 'CONVERTED') {
          return (
            <span className="px-3 py-1 rounded-full bg-[#C0FFEA] text-[#17805C] text-[9px] font-bold uppercase whitespace-nowrap">
              {t('participantDetail.offerInteractionStatus.converted')}
            </span>
          );
        }
        if (status === 'CLICKED') {
          return (
            <span className="px-3 py-1 rounded-full bg-[#EAF0FF] text-[#1A4199] text-[9px] font-bold uppercase whitespace-nowrap">
              {t('participantDetail.offerInteractionStatus.clicked')}
            </span>
          );
        }
        if (status === 'NO_CLICK') {
          return (
            <span className="px-3 py-1 rounded-full bg-theme-light text-role-secondary text-[9px] font-bold uppercase whitespace-nowrap">
              {t('participantDetail.offerInteractionStatus.noClick')}
            </span>
          );
        }
        return <span className="text-role-tertiary text-xs px-3">—</span>;
      },
    }),
    columnHelper.accessor('relativeScorePercentage', {
      header: tA('participantDetail.table.score'),
      cell: (info) => (
        <div className="flex items-center gap-3 min-w-[120px]">
          <span className="font-bold text-dark text-sm w-8">{info.getValue()}%</span>
          <div className="flex-1 h-1.5 bg-theme-light rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-700"
              style={{ width: `${info.getValue()}%` }}
            />
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('watchTimeDuration', {
      header: tA('participantDetail.table.presence'),
      cell: (info) => (
        <span className="text-role-default font-medium text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.display({
      id: 'phone',
      header: tA('participantDetail.table.phone'),
      cell: (info) => (
        <span className="text-role-secondary text-sm">
          {info.row.original.phoneNumber || tA('participantDetail.table.noPhone')}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <button
          onClick={() => handleOpenParticipantDetails(info.row.original.attendeeId)}
          className="px-4 py-1.5 bg-white border cursor-pointer hover:shadow border-medium rounded-lg text-xs font-bold text-dark hover:bg-theme-light transition-all shadow-sm"
        >
          {tA('participantDetail.table.view')}
        </button>
      ),
    }),
  ];

  const table = useReactTable({
    data: participants,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="w-full bg-white border border-medium rounded-[20px] overflow-hidden shadow-sm">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead className="surface-subtle border-b border-medium">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-5 text-role-tertiary uppercase text-[10px] tracking-widest font-bold"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-medium">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-role-tertiary text-sm">
                    {tA('participantDetail.table.loading')}
                  </td>
                </tr>
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.original.attendeeId}
                    className="hover:bg-theme-light/50 transition-colors group cursor-default"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-role-tertiary text-sm">
                    {tA('participantDetail.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 px-2">
          <p className="text-role-tertiary text-xs font-medium">
            {tA('participantDetail.table.showing', {
              shown: participants.length,
              total: totalElements,
            })}
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};
